import { useEffect, useState } from "react";
import { Card, ErrorBanner, Pill } from "../components/ui";
import { api, type Campaign } from "../lib/api";

type TriggerParam = { key: string; label: string; type: string; default: unknown };
type Trigger = {
  key: string;
  label: string;
  hint: string;
  live: boolean;
  /** What this trigger needs before it can match anything. The API has always
   *  declared these; this screen used to ignore them and post the trigger
   *  alone, so a "tag is added" rule built here carried no tag - it fired on
   *  EVERY tag, which moved people between campaigns on any CRM sync. */
  params?: TriggerParam[];
  needs_condition?: boolean;
};
type Transition = {
  id: number;
  from_campaign_id: number | null;
  from_campaign: string;
  trigger: string;
  to_campaign_id: number | null;
  to_campaign: string;
  delay_minutes: number;
  priority: number;
  enabled: boolean;
};

const WHEN = [
  { minutes: 0, label: "straight away" },
  { minutes: 60, label: "an hour later" },
  { minutes: 1440, label: "the next day" },
  { minutes: 2880, label: "two days later" },
  { minutes: 10080, label: "a week later" },
  { minutes: 43200, label: "thirty days later" },
];

export function Journeys() {
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [rules, setRules] = useState<Transition[]>([]);
  const [camps, setCamps] = useState<Campaign[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [from, setFrom] = useState<string>("");
  const [trigger, setTrigger] = useState("reply_received");
  const [to, setTo] = useState<string>("");
  const [delay, setDelay] = useState(1440);
  //: Values for whatever the chosen trigger declares it needs.
  const [params, setParams] = useState<Record<string, string>>({});

  const load = () =>
    Promise.all([api.transitions(), api.campaigns()])
      .then(([t, c]) => {
        setTriggers(t.triggers);
        setRules(t.transitions);
        setCamps(c);
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const add = () => {
    setBusy(true);
    api
      .createTransition({
        from_campaign_id: from ? Number(from) : null,
        trigger,
        to_campaign_id: to ? Number(to) : null,
        delay_minutes: delay,
        trigger_params: Object.keys(params).length ? params : undefined,
      })
      .then(load)
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const remove = (id: number) =>
    api.deleteTransition(id).then(load).catch((e) => setError(e.message));

  const label = (key: string) => triggers.find((t) => t.key === key);

  // A rule missing what its trigger needs is worse than no rule: it looks
  // configured on this screen and matches nobody in the engine. A condition
  // cannot be built here at all, so that one is sent to the campaign editor
  // rather than created empty.
  const spec = label(trigger);
  const missing = (spec?.params ?? []).filter(
    (p) => !String(params[p.key] ?? p.default ?? "").trim(),
  );
  const canAdd = !spec?.needs_condition && missing.length === 0;

  return (
    <>
      <ErrorBanner error={error} />
      <h2 style={{ fontSize: 19 }}>Journeys</h2>
      <p className="dim" style={{ marginTop: -4, fontSize: 13, maxWidth: 720 }}>
        What happens to someone when they act. These rules are read at the moment the
        thing happens, so changing one changes the funnel immediately — no deploy, and
        every change is on the audit log.
      </p>

      <Card title="Add a rule">
        <div className="row w" style={{ gap: 8, alignItems: "center" }}>
          <span className="lab">When someone in</span>
          <select className="f" value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">any campaign</option>
            {camps.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            className="f"
            value={trigger}
            onChange={(e) => {
              setTrigger(e.target.value);
              setParams({});
            }}
          >
            {triggers.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
                {t.live ? "" : " (not firing yet)"}
              </option>
            ))}
          </select>

          {/* What the chosen trigger needs. Without these the rule is
              created and silently matches nothing (or, before the engine
              guard, everything). */}
          {(label(trigger)?.params ?? []).map((p) => (
            <span key={p.key} className="lab" style={{ display: "contents" }}>
              <span className="lab">{p.label}</span>
              <input
                className="f"
                type={p.type === "number" ? "number" : "text"}
                value={params[p.key] ?? String(p.default ?? "")}
                placeholder={p.type === "tag" ? "tag key" : ""}
                onChange={(e) =>
                  setParams({ ...params, [p.key]: e.target.value })
                }
              />
            </span>
          ))}

          <span className="lab">move them to</span>
          <select className="f" value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">nothing — just exit</option>
            {camps.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            className="f"
            value={delay}
            onChange={(e) => setDelay(Number(e.target.value))}
          >
            {WHEN.map((w) => (
              <option key={w.minutes} value={w.minutes}>
                {w.label}
              </option>
            ))}
          </select>

          <button className="btn p" disabled={busy || !canAdd} onClick={add}>
            {busy ? "…" : "Add"}
          </button>
        </div>

        {spec?.needs_condition && (
          <div className="banner warn stack" style={{ marginTop: 12 }}>
            <b>A condition cannot be written here.</b> {spec.label} needs a
            condition to test, and this screen only has room for the four
            fields above. Build it on the campaign's own page, where the rule
            editor has the full condition builder. Creating it here would make
            a rule that looks right on this list and matches nobody.
          </div>
        )}

        {missing.length > 0 && (
          <div className="banner warn stack" style={{ marginTop: 12 }}>
            <b>Needs {missing.map((m) => m.label).join(" and ")}.</b> A{" "}
            {spec?.label?.toLowerCase()} rule with no{" "}
            {missing.map((m) => m.key).join(" or ")} matches nobody, so it
            cannot be added until you fill that in.
          </div>
        )}

        {!label(trigger)?.live && (
          <div className="banner warn stack" style={{ marginTop: 12 }}>
            <b>{label(trigger)?.label} does not fire yet.</b> {label(trigger)?.hint} The
            rule saves and will start working the moment that feed is connected — it
            just will not do anything today.
          </div>
        )}
      </Card>

      <Card title="The rules, in the order they are checked">
        {rules.length === 0 ? (
          <div className="empty">
            No rules yet. Without any, a reply exits someone from their campaign and
            they stop there — nothing picks them up.
          </div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>When someone in</th>
                  <th>does this</th>
                  <th>they go to</th>
                  <th>after</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} style={{ opacity: r.enabled ? 1 : 0.5 }}>
                    <td>{r.from_campaign}</td>
                    <td>
                      <Pill tone={label(r.trigger)?.live ? "ok" : "nu"}>
                        {label(r.trigger)?.label ?? r.trigger}
                      </Pill>
                    </td>
                    <td>
                      <b>{r.to_campaign}</b>
                    </td>
                    <td className="dim mono" style={{ fontSize: 11.5 }}>
                      {WHEN.find((w) => w.minutes === r.delay_minutes)?.label ??
                        `${r.delay_minutes} min`}
                    </td>
                    <td>
                      <button className="x" onClick={() => remove(r.id)}>
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Always true, whatever you set here">
        <div className="col" style={{ gap: 8, fontSize: 12.5 }}>
          <div className="row w">
            <Pill tone="ba">Opt-out</Pill>
            <span className="dim">
              exits every campaign and blocks all future sending. Cannot be overridden,
              by anyone.
            </span>
          </div>
          <div className="row w">
            <Pill tone="ac">One nurture at a time</Pill>
            <span className="dim">
              a contact cannot sit in two nurture campaigns at once — the database
              refuses it, so a bad rule cannot double-text someone.
            </span>
          </div>
        </div>
      </Card>
    </>
  );
}
